import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Zap, TrendingUp, Calendar, AlertTriangle, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import type { PiekAnalyse } from '@/types/piekanalyse';

interface OrganizationAnalysesListProps {
  organizationId: string;
}

export const OrganizationAnalysesList: React.FC<OrganizationAnalysesListProps> = ({
  organizationId,
}) => {
  const navigate = useNavigate();

  const { data: analyses = [], isLoading } = useQuery({
    queryKey: ['org-piek-analyses', organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('piek_analyses')
        .select('*')
        .eq('organization_id', organizationId)
        .order('analysis_year', { ascending: false });
      
      if (error) throw error;
      return data as unknown as PiekAnalyse[];
    },
    enabled: !!organizationId,
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (analyses.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Zap className="h-10 w-10 mx-auto mb-3 opacity-30" />
        <p>Nog geen piekvermogen analyses gekoppeld</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => navigate('/crm/piekvermogen')}
        >
          Nieuwe analyse maken
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {analyses.map((analysis) => {
        const config = analysis.config || { contractedCapacity: 0 };
        const isAboveContract = analysis.highest_peak_kwp > config.contractedCapacity;

        return (
          <Card
            key={analysis.id}
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate(`/crm/analyses/piekvermogen/${analysis.id}`)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold">{analysis.name}</h4>
                    {analysis.analysis_year && (
                      <Badge variant="outline">
                        <Calendar className="h-3 w-3 mr-1" />
                        {analysis.analysis_year}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      Piek: <strong className={isAboveContract ? 'text-destructive' : ''}>{analysis.highest_peak_kwp} kWp</strong>
                    </span>
                    <span className="flex items-center gap-1">
                      <Zap className="h-3 w-3" />
                      Contract: {config.contractedCapacity} kWp
                    </span>
                    {analysis.total_exceedances > 0 && (
                      <span className="flex items-center gap-1 text-amber-600">
                        <AlertTriangle className="h-3 w-3" />
                        {analysis.total_exceedances} overschrijdingen
                      </span>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="icon">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default OrganizationAnalysesList;
